import { Alert, Button, Group, Modal, Radio, Stack, TextInput } from "@mantine/core";
import { useForm } from "@mantine/form";
import { useCallback, useMemo, type FC } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { z } from "zod";
import { useTRPC } from "../../../../trpc";
import { useNavigate } from "@tanstack/react-router";
import { IconAlertTriangle } from "@tabler/icons-react";
import { useMutation } from "@tanstack/react-query";
import { zod4Resolver } from "mantine-form-zod-resolver";

interface DeleteAccountModalProps {
  open: boolean;
  onClose: () => void;
}

interface FormValuesCloseAccount {
  [key: string]: string | undefined;
  comment?: string;
  feedback: "customer_service" | "low_quality" | "missing_features" | "other" | "switched_service" | "too_complex" | "too_expensive" | "unused";
}

export const DeleteAccountModal: FC<DeleteAccountModalProps> = ({ onClose, open }) => {
  const intl = useIntl();
  const navigate = useNavigate();
  const trpc = useTRPC();
  const { mutate: closeCurrentAccount, isPending } = useMutation(trpc.closeCurrentAccount.mutationOptions());

  const validate = useMemo(() => zod4Resolver(z.object({
    comment: z.string().min(3, {
      message: intl.formatMessage({ defaultMessage: "Comment should have at least 3 characters", id: "error.commentTooShort3Chars" }),
    }),
  })), [intl]);

  const { onSubmit, getInputProps: getInputPropsCloseAccount } = useForm({
    validateInputOnBlur: true,
    initialValues: {
      feedback: "other" as const,
      comment: undefined,
    },
    validate,
  });

  const handleSubmitCloseAccount = useCallback((form: FormValuesCloseAccount) => {
    closeCurrentAccount(form, {
      onSuccess: () => {
        onClose();
        void navigate({ to: "/sign-in" });
      },
    });
  }, [closeCurrentAccount, onClose, navigate]);

  return (
    <Modal opened={open} onClose={onClose} title={intl.formatMessage({ defaultMessage: "Close account", id: "title.closeAccount" })}>
      <form onSubmit={onSubmit(handleSubmitCloseAccount)}>
        <Stack gap="xs">
          <FormattedMessage
            id="text.closeAccountDescription"
            defaultMessage="Are you sure you want to close your account? Once closed, you will not be able to access your account and all associated data will be deleted."
          />
          <Alert variant="light" color="red" icon={<IconAlertTriangle />}>
            <FormattedMessage
              defaultMessage="We will keep account data for period for 60 days following account closure"
              id="text.deleteAccountDataConsequences"
            />
          </Alert>

          <Radio.Group
            label={intl.formatMessage({ defaultMessage: "Select reason for your decision", id: "label.selectReasonForYourDecision" })}
            required
            {...getInputPropsCloseAccount("feedback")}
          >
            <Stack mt="xs">
              <Radio value="customer_service" label={intl.formatMessage({ defaultMessage: "Customer service", id: "label.customerService" })} />
              <Radio value="low_quality" label={intl.formatMessage({ defaultMessage: "Low quality", id: "label.lowQuality" })} />
              <Radio value="switched_service" label={intl.formatMessage({ defaultMessage: "Switched service", id: "label.switchedService" })} />
              <Radio value="too_complex" label={intl.formatMessage({ defaultMessage: "Too complex", id: "label.tooComplex" })} />
              <Radio value="too_expensive" label={intl.formatMessage({ defaultMessage: "Too expensive", id: "label.tooExpensive" })} />
              <Radio value="unused" label={intl.formatMessage({ defaultMessage: "Unused", id: "label.unused" })} />
              <Radio value="other" label={intl.formatMessage({ defaultMessage: "Other", id: "label.other" })} />
            </Stack>
          </Radio.Group>

          <TextInput
            label={intl.formatMessage({ defaultMessage: "Comment", id: "label.comment" })}
            {...getInputPropsCloseAccount("comment")}
          />
          <Group gap="xs" justify="flex-end" pt="lg">
            <Button variant="secondary" type="reset" loading={isPending} onClick={onClose}>
              <FormattedMessage defaultMessage="Cancel" id="button.cancel" />
            </Button>

            <Button variant="danger" type="submit" loading={isPending}>
              <FormattedMessage defaultMessage="Close account" id="button.closeAccount" />
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
};
